/**
 * File: apps/qbench-cp/reactapp/src/shared/layout/PortalMainMenu/OutstandingBalance.js
 *
 * Purpose: Renders a link showing the customer's unpaid invoice balance, linking to a
 *     pre-filtered invoices list.
 *
 * Scope: Portal top navigation billing indicator for the customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Reads billingEnabled from app state and the caller's read-invoice privilege via
 *     useAccessControl, rendering nothing if billing is disabled or the user lacks permission.
 *     Fetches the unpaid balance with useBillingMetrics and formats it with QBenchCurrency.
 *     Renders a mobile-styled variant or a desktop variant depending on useResponsive, both
 *     linking to /portal/invoices filtered to UNPAID status. Clicking while inside the
 *     create-order flow dispatches a RESET_CREATE_ORDER action to redirect there instead of
 *     navigating directly; an optional toggleModal callback closes a parent menu on click.
 *
 * Exports: OutstandingBalance component (default export).
 */

import { useAppStore } from 'App/store';
import { Link, useLocation } from 'react-router-dom';
import QBenchCurrency from 'shared/components/QBenchCurrency';
import permissions from 'shared/constants/permissions';
import { useBillingMetrics } from 'shared/hooks/api/misc';
import useAccessControl from 'shared/hooks/misc/useAccessControl';
import useResponsive from 'shared/hooks/misc/useResponsive';
import { noop } from 'shared/utils/misc';
import styled from 'styled-components';
import { CREATE_ORDER_PATH } from '../MainMenu';

const { read } = permissions;

const Wrapper = styled(Link)`
  font-size: 0.875rem;
  font-weight: 500;
  padding-bottom: 2px;
  border-color: ${props => props.theme.color.palette.text.headerText}; 
  &, &:hover {
    ${props => props.$isMobile
      ? `color: ${props.theme.color.palette.grey[600]}`
      : `color: ${props.theme.color.palette.text.headerText}`
    }
  }
`;

export default function OutstandingBalance({ toggleModal = noop }) {
  const { pathname } = useLocation();
  const { isMobile } = useResponsive();
  const { appState: { billingEnabled }, appStateDispatch } = useAppStore();
  const { data: { unpaid_balance } } = useBillingMetrics();
  const hasPermission  = useAccessControl({ privilege: [read.invoice] });

  const toPath = {
    pathname: '/portal/invoices',
    search: '?statuses=UNPAID'
  };

  function handlePreClick(e) {
    if (pathname.includes(CREATE_ORDER_PATH)) {
      e.preventDefault();
      appStateDispatch({
        type: 'RESET_CREATE_ORDER',
        payload: `${toPath.pathname}${toPath.search}`,
      });
    } else {
      appStateDispatch({ type: 'RESET_CREATE_ORDER', payload: false });
    }
  }

  if (!billingEnabled) return null;
  if (!hasPermission) return null;

  if (isMobile) return (
    <Wrapper $isMobile={isMobile} className="d-block lh-1 my-3 mb-4 text-center text-decoration-none" to={toPath} onClick={toggleModal}> 
      <span>Balance: <QBenchCurrency quantity={unpaid_balance ?? 0} /></span>
    </Wrapper>
  );


  return (
    <Wrapper $isMobile={isMobile} className="d-none px-4 lh-1 border-start d-lg-flex align-items-center text-decoration-none" to={toPath} onClick={handlePreClick}>
      <span className="d-none d-xl-inline">Balance:&nbsp;</span>
      <span><QBenchCurrency quantity={unpaid_balance ?? 0} /></span>
    </Wrapper>
  );
}

